<script setup>
import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import SmsPartnerAPI from 'dashboard/api/smsPartner';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';

// Painel de configuração da operadora SMS Shortcode — versão SIMPLES: só URL + token.
// O segredo do webhook (dlr_token), o número remetente e o nome da caixa são gerados/
// preenchidos automaticamente no backend. Quando validado, libera a criação de
// campanhas SMS (gating na página).
const emit = defineEmits(['saved']);

const dialogRef = ref(null);
const status = ref({});
const form = ref({ token: '', base_url: '' });
const saving = ref(false);
const validating = ref(false);

const load = async () => {
  try {
    const { data } = await SmsPartnerAPI.get();
    status.value = data || {};
    form.value.base_url = data?.base_url || '';
  } catch (e) {
    status.value = {};
  }
};

const open = async () => {
  await load();
  dialogRef.value?.open();
};

const save = async () => {
  saving.value = true;
  try {
    const { data } = await SmsPartnerAPI.create(form.value);
    status.value = data;
    form.value.token = '';
    useAlert('Parceiro salvo. Agora valide a conexão.');
    emit('saved');
  } catch (e) {
    useAlert('Não foi possível salvar o parceiro.');
  } finally {
    saving.value = false;
  }
};

const validate = async () => {
  validating.value = true;
  try {
    const { data } = await SmsPartnerAPI.validate();
    status.value = data;
    useAlert(
      data?.test?.ok
        ? 'Conexão validada! Já dá para criar campanhas de SMS.'
        : `Falha na validação: ${data?.test?.error || 'erro desconhecido'}`
    );
    emit('saved');
  } catch (e) {
    useAlert('Não foi possível validar a conexão.');
  } finally {
    validating.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    title="Configurar parceiro (SMS Shortcode)"
    :show-confirm-button="false"
    cancel-button-label="Fechar"
    width="xl"
  >
    <div class="flex flex-col gap-3">
      <div
        class="flex items-center gap-2 text-xs"
        :class="status.validated ? 'text-n-teal-11' : 'text-n-amber-11'"
      >
        <span
          :class="
            status.validated
              ? 'i-ri-checkbox-circle-line'
              : 'i-ri-error-warning-line'
          "
        />
        <span v-if="status.validated">Conectado e validado</span>
        <span v-else-if="status.configured">Configurado — falta validar a conexão</span>
        <span v-else>Ainda não configurado</span>
      </div>

      <p class="text-xs text-n-slate-11">
        Só precisa de duas coisas: o <strong>token de API</strong> da operadora e,
        opcionalmente, a <strong>URL</strong> da API. O resto é configurado
        automaticamente.
      </p>

      <div class="flex flex-col gap-1">
        <Input
          v-model="form.token"
          label="Token de API"
          type="password"
          :placeholder="status.has_token ? '•••••• (mantém o atual se vazio)' : 'Cole o token da operadora Short Code'"
        />
        <p class="text-xs text-n-slate-10">
          Pegue no painel da Short Code (Configurações → API). Em branco mantém o token atual.
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <Input
          v-model="form.base_url"
          label="URL da API (opcional)"
          placeholder="https://api.shortcode.com.br"
        />
        <p class="text-xs text-n-slate-10">Deixe vazio para usar o padrão da operadora.</p>
      </div>

      <Accordion
        v-if="status.webhook_url"
        title="Avançado — receber respostas (opcional)"
        :is-open="false"
      >
        <div class="flex flex-col gap-1">
          <p class="text-xs text-n-slate-10">
            Só precisa fazer isto uma vez, e apenas se quiser receber respostas dos
            clientes (ex.: cliente responde "SIM" e a IA assume no WhatsApp) e o status
            de entrega. Cole a URL abaixo no painel da operadora, no evento
            "Recebimento de SMS". O segredo já vem embutido e é gerenciado automaticamente.
          </p>
          <code class="text-xs text-n-slate-11 break-all">{{ status.webhook_url }}</code>
        </div>
      </Accordion>

      <div class="flex gap-2 pt-1">
        <Button
          label="Salvar"
          size="sm"
          :is-loading="saving"
          @click="save"
        />
        <Button
          label="Validar conexão"
          variant="faded"
          color="slate"
          size="sm"
          :is-loading="validating"
          :disabled="!status.configured"
          @click="validate"
        />
      </div>
    </div>
  </Dialog>
</template>
